import { useCallback, useEffect, useState } from "react";
import TokenStore from "services/TokenStore";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import {
  isFeatureEnabled,
  FEATURE_FLAGS,
} from "components/helpers/GatedComponent";

const STORAGE_KEY = "faye.use-digital-ux";
const CHANGE_EVENT = "faye.use-digital-ux-changed";

const readOverride = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const subscribeToOverride = (onChange: () => void) => {
  window.addEventListener("storage", onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
};

// Non-digital accounts with the SMS Bot feature flag can preview the digital
// subscription UX via a local override.
export const canPreviewDigitalUX = (): boolean => {
  const currentUser = TokenStore.getLocalCurrentUser();
  if (!currentUser) {
    return false;
  }
  const { isDigitalSubscription } = planFlagsFromStatus(
    currentUser.familyStatus,
  );
  return (
    !isDigitalSubscription && isFeatureEnabled(FEATURE_FLAGS.SMS_BOT.value)
  );
};

export const useDigitalUXOverride = (): [
  boolean,
  (enabled: boolean) => void,
] => {
  const [override, setOverrideState] = useState<boolean>(readOverride);
  useEffect(() => {
    const onChange = () => setOverrideState(readOverride());
    return subscribeToOverride(onChange);
  }, []);
  const setOverride = useCallback((enabled: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch {
      return;
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);
  return [override, setOverride];
};

export default (): boolean => {
  const [override] = useDigitalUXOverride();
  const currentUser = TokenStore.getLocalCurrentUser();
  const { isDigitalSubscription } = planFlagsFromStatus(
    currentUser?.familyStatus,
  );
  return isDigitalSubscription || (canPreviewDigitalUX() && override);
};
